import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api, getUserId } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, Building2, Loader2 } from "lucide-react";

export default function NewBriefingPage() {
  const navigate   = useNavigate();
  const userId     = getUserId();
  const [accounts, setAccounts]           = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [accountsError, setAccountsError] = useState(null);
  const [topId, setTopId]                 = useState("");
  const [children, setChildren]           = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [childrenError, setChildrenError] = useState(null);
  const [childId, setChildId]             = useState("");
  const [lookbackDays, setLookbackDays]   = useState(365);
  const [submitting, setSubmitting]       = useState(false);

  useEffect(() => {
    if (!userId) { navigate("/"); return; }
    (async () => {
      try {
        const { accounts: items } = await api.listAccounts(userId);
        setAccounts(items || []);
        const firstDirect = (items || []).find(a => !a.is_manager);
        if (firstDirect) setTopId(firstDirect.customer_id);
      } catch (e) {
        setAccountsError(e?.response?.data?.detail || "Couldn't list your Google Ads accounts.");
      } finally {
        setLoadingAccounts(false);
      }
    })();
  }, [userId, navigate]);

  const selectedTop = accounts.find(a => a.customer_id === topId);
  const isMcc = !!selectedTop?.is_manager;

  useEffect(() => {
    if (!isMcc) { setChildren([]); setChildId(""); setChildrenError(null); return; }
    let cancelled = false;
    (async () => {
      setLoadingChildren(true);
      setChildrenError(null);
      setChildId("");
      try {
        const { accounts: items } = await api.listMccClients(userId, topId);
        if (cancelled) return;
        setChildren(items || []);
      } catch (e) {
        if (cancelled) return;
        setChildrenError(e?.response?.data?.detail || "Couldn't list accounts under this manager.");
      } finally {
        if (!cancelled) setLoadingChildren(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isMcc, topId, userId]);

  const effectiveCustomerId = isMcc ? childId : topId;
  const managerCustomerId   = isMcc ? topId : null;

  const submit = async () => {
    if (!effectiveCustomerId) return toast.error("Select an account first.");
    setSubmitting(true);
    try {
      const { job_id } = await api.generateBriefing({
        user_id: userId,
        customer_id: effectiveCustomerId,
        manager_customer_id: managerCustomerId,
        lookback_days: lookbackDays,
        goal: "full_audit",
        business_context: {},
      });
      navigate(`/jobs/${job_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start briefing.");
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 py-12 md:px-10 md:py-16">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">new briefing</p>
        <h1 className="mt-2 font-heading text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">
          Pick an account.
        </h1>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-slate-600">
          HandOff pulls a full year of data and gives you a complete account read.
        </p>

        <div className="mt-8 card-soft p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-100 text-slate-600">
              <Building2 size={16} strokeWidth={1.8} />
            </div>
            <h3 className="font-heading text-base font-medium text-slate-900">Google Ads account</h3>
          </div>

          {loadingAccounts ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 size={14} className="animate-spin" /> Loading accounts…
            </div>
          ) : accountsError ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              <p className="font-medium">We couldn't reach Google Ads.</p>
              <p className="mt-1 text-xs">{accountsError}</p>
            </div>
          ) : accounts.length === 0 ? (
            <p className="text-sm text-slate-500">No accessible accounts on this Google login.</p>
          ) : (
            <>
              <select
                value={topId || ""}
                onChange={(e) => setTopId(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-[#0052FF] focus:outline-none focus:ring-2 focus:ring-[#0052FF]/20"
              >
                <option value="" disabled>Select an account…</option>
                {accounts.map(acc => (
                  <option key={acc.customer_id} value={acc.customer_id}>
                    {(acc.descriptive_name || `Customer ${acc.customer_id}`)}
                    {" · "}{formatCid(acc.customer_id)}
                    {acc.is_manager ? " (MCC)" : acc.currency_code ? ` · ${acc.currency_code}` : ""}
                  </option>
                ))}
              </select>

              {isMcc && (
                <div className="mt-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Account under {selectedTop.descriptive_name || formatCid(topId)}
                  </p>
                  {loadingChildren ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Loader2 size={14} className="animate-spin" /> Loading client accounts…
                    </div>
                  ) : childrenError ? (
                    <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                      <p className="font-medium">Couldn't list client accounts.</p>
                      <p className="mt-1 text-xs">{childrenError}</p>
                    </div>
                  ) : children.length === 0 ? (
                    <p className="text-sm text-slate-500">No active client accounts under this manager.</p>
                  ) : (
                    <select
                      value={childId || ""}
                      onChange={(e) => setChildId(e.target.value)}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-[#0052FF] focus:outline-none focus:ring-2 focus:ring-[#0052FF]/20"
                    >
                      <option value="" disabled>Select a client account…</option>
                      {children.map(acc => (
                        <option key={acc.customer_id} value={acc.customer_id}>
                          {(acc.descriptive_name || `Customer ${acc.customer_id}`)}
                          {" · "}{formatCid(acc.customer_id)}
                          {acc.currency_code ? ` · ${acc.currency_code}` : ""}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
            <button
              onClick={() => setLookbackDays(90)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                lookbackDays === 90
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Last 90 days
            </button>
            <button
              onClick={() => setLookbackDays(365)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                lookbackDays === 365
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Last 12 months
            </button>
          </div>
          <button
            onClick={submit}
            disabled={submitting || !effectiveCustomerId || loadingAccounts || loadingChildren}
            className="inline-flex items-center gap-2 rounded-md bg-[#0052FF] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0040CC] disabled:opacity-60"
          >
            {submitting ? (
              <><Loader2 size={15} className="animate-spin" /> Starting…</>
            ) : (
              <>Generate briefing <ArrowRight size={15} /></>
            )}
          </button>
        </div>
      </div>
    </Layout>
  );
}

function formatCid(cid) {
  if (!cid) return "";
  const s = String(cid);
  if (s.length !== 10) return s;
  return `${s.slice(0, 3)}-${s.slice(3, 6)}-${s.slice(6)}`;
}
